import { Avatar, AvatarProps, Skeleton } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import GamePlaceholder from '../../assets/GamePlaceholder.svg?url'
import useGameIcon from '../../hooks/useGameIcon'

interface GameAvatarProps extends Omit<AvatarProps, 'src' | 'children'> {
  gameId?: string
}

const GameAvatar = ({ gameId, sx, ...avatarProps }: GameAvatarProps) => {
  const { data: gameIconUrl, isFetched } = useGameIcon(gameId)
  const [imagePreloaded, setImagePreloaded] = useState(false)
  const [avatarRendered, setAvatarRendered] = useState(false)

  const showSkeleton = useMemo(
    () =>
      gameId &&
      (!isFetched || (gameIconUrl && (!imagePreloaded || !avatarRendered))),
    [gameId, isFetched, gameIconUrl, imagePreloaded, avatarRendered]
  )

  const avatarSrc = useMemo(() => {
    if (!gameId || (isFetched && !gameIconUrl)) {
      return GamePlaceholder
    }
    if (gameIconUrl) {
      return gameIconUrl
    }
    return undefined
  }, [gameId, gameIconUrl, isFetched])

  const skeletonSx = useMemo(() => {
    if (!sx) return undefined
    const { border, ...restSx } = sx as any
    return restSx
  }, [sx])

  const avatarStyles = useMemo(
    () => ({
      ...sx,
      opacity: showSkeleton ? 0 : 1,
      position: showSkeleton ? ('absolute' as const) : ('static' as const),
      visibility: showSkeleton ? ('hidden' as const) : ('visible' as const),
    }),
    [sx, showSkeleton]
  )

  const handleAvatarLoad = useMemo(() => () => setAvatarRendered(true), [])

  const handleAvatarError = useMemo(() => () => setAvatarRendered(true), [])

  useEffect(() => {
    if (!gameIconUrl) {
      setImagePreloaded(true)
      setAvatarRendered(true)
      return
    }

    setImagePreloaded(false)
    setAvatarRendered(false)

    const img = new Image()
    img.onload = () => setImagePreloaded(true)
    img.onerror = () => setImagePreloaded(true)
    img.src = gameIconUrl
  }, [gameIconUrl])

  return (
    <>
      {showSkeleton && (
        <Skeleton variant='circular' animation='wave' sx={skeletonSx} />
      )}
      <Avatar
        src={avatarSrc}
        sx={avatarStyles}
        onLoad={handleAvatarLoad}
        onError={handleAvatarError}
        {...avatarProps}
      />
    </>
  )
}

export default GameAvatar
